import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, InputNumber, Message } from '@arco-design/web-react';
import get from 'lodash/get';
import set from 'lodash/set';

import { OptionMetaRootField } from '../../platform/option-meta';
import { isVendorQuantityOptional } from '../../utils/postPaidPricing';
import { PluginModal } from '../PluginModal';
import { DEFAULT_QUANTITY_UNITS, useWatchedQuantityUnit } from '../quantityUnit';
import { hasVendorDeviceSelection, resolveVendorDeviceRequiredValidateField } from '../vendorAndDeviceRadioList';
import { VendorDevicePersistFields, readVendorDeviceMeta } from '../vendorDevice';
import { VendorDeviceSelector } from './VendorDeviceSelector';
import styles from './index.module.less';
import type { ResourceConfigModalProps } from './types';

export interface ResourceConfigModalChildrenContext {
  /** 当前选型 quantityUnit；无则默认单位 */
  quantityUnit: string;
}

/** 资源配置弹窗：厂商设备选择 + 自定义规格区 / 数量输入 */
export function ResourceConfigModal({
  visible,
  title,
  resourceCode,
  vendorDevicePrefix,
  initialValues,
  billModelId,
  deploymentTypeId,
  paymentTiming,
  skuType,
  resourceEditMode,
  architecture,
  validateFields,
  canSubmit,
  onOk,
  onCancel,
  children,
  quantityField,
  quantityLabel,
  quantityDefaultUnit = DEFAULT_QUANTITY_UNITS.gpu,
  lockQuantityDisplayToDefault = false,
  inventoryResourceType,
  workloadType,
  inventoryShowQuantity,
  isContainerGpu,
  requireGpuSoftware = !isContainerGpu,
  vendorDeviceHidden,
  vendorDeviceDisabled,
  vendorDeviceSkipFetch,
}: ResourceConfigModalProps) {
  const { t } = useTranslation();
  const [modalForm] = Form.useForm();
  const hasInitializedRef = useRef(false);
  const skipQuantityRequired = isVendorQuantityOptional({ billModelId, paymentTiming, skuType, resourceEditMode });
  const effectiveVendorSkipFetch = vendorDeviceSkipFetch ?? (Boolean(vendorDeviceDisabled) || resourceEditMode === 'order-fixed');
  const quantityUnit = useWatchedQuantityUnit(modalForm, billModelId ? vendorDevicePrefix : undefined, quantityDefaultUnit, {
    lockDisplayToDefault: lockQuantityDisplayToDefault,
  });

  useEffect(() => {
    if (!visible) {
      hasInitializedRef.current = false;
      modalForm.resetFields();
      return;
    }

    if (!hasInitializedRef.current) {
      const merged = { ...(initialValues ?? {}) };
      /** flatten 点路径与 lodash get 嵌套路径都要覆盖，避免误覆盖已回填数量 */
      const existingQty = quantityField != null ? (merged[quantityField] ?? get(merged, quantityField)) : undefined;
      /** GPU 数量：空值默认 1（含后付费 / 自定义规格创建） */
      if (billModelId && quantityField && (existingQty == null || existingQty === '')) {
        merged[quantityField] = 1;
        set(merged, quantityField, 1);
      }
      modalForm.setFieldsValue(merged);
      hasInitializedRef.current = true;
    }
  }, [visible, initialValues, modalForm, billModelId, quantityField]);

  const handleOk = async () => {
    const vendorDeviceRequiredField =
      billModelId && !vendorDeviceHidden ? resolveVendorDeviceRequiredValidateField(vendorDevicePrefix) : undefined;
    const listedValidateFields = validateFields ?? [];
    const fieldsToValidate = [
      ...(skipQuantityRequired ? [] : listedValidateFields),
      /** quantityField 仅 GPU：始终必填（后付费 / 自定义规格创建也不放宽） */
      ...(quantityField && (skipQuantityRequired || !listedValidateFields.includes(quantityField)) ? [quantityField] : []),
      ...(vendorDeviceRequiredField ? [vendorDeviceRequiredField] : []),
    ];

    if (fieldsToValidate.length) {
      try {
        await modalForm.validate(fieldsToValidate);
      } catch {
        return Promise.reject();
      }
    }

    /** getFieldsValue 可能漏 PersistFields / 校验叶子；与完整 store 合并后再提交 */
    const nested = modalForm.getFieldsValue() as Record<string, unknown>;
    const store = modalForm.getFields() as Record<string, unknown>;
    const modalValues: Record<string, unknown> = { ...nested };
    const mergeField = (field: string) => {
      const value = nested[field] ?? get(store, field) ?? get(nested, field);
      if (value !== undefined) {
        modalValues[field] = value;
      }
    };
    (validateFields ?? []).forEach(mergeField);
    if (quantityField) mergeField(quantityField);
    const pricing = readVendorDeviceMeta(store, vendorDevicePrefix) || readVendorDeviceMeta(nested, vendorDevicePrefix);
    if (pricing) {
      Object.assign(modalValues, {
        [vendorDevicePrefix]: { ...((nested[vendorDevicePrefix] as object) || {}), ...pricing },
        ...Object.fromEntries(Object.entries(pricing).map(([key, value]) => [`${vendorDevicePrefix}.${key}`, value])),
      });
    }
    if (billModelId && !vendorDeviceHidden) {
      const selection = readVendorDeviceMeta(modalValues, vendorDevicePrefix);
      if (!hasVendorDeviceSelection(selection)) {
        Message.warning(t('data-center:vendor.device.selection.required'));
        return Promise.reject();
      }
    }

    if (quantityField) {
      const qty = modalValues[quantityField] ?? get(modalValues, quantityField);
      if (qty == null || qty === '') {
        Message.warning(t('common:required.field'));
        return Promise.reject();
      }
    }

    if (canSubmit && !canSubmit(modalValues)) {
      Message.warning(
        inventoryResourceType || (billModelId && !vendorDeviceHidden)
          ? t('data-center:vendor.device.selection.required')
          : t('common:required.field'),
      );
      return Promise.reject();
    }
    await onOk(modalValues);
  };

  const showVendorDevice = Boolean(billModelId) || inventoryResourceType;
  /** 数量始终展示；后付费 / 自定义规格创建仅跳过必填 */
  const showPricingQuantity = Boolean(billModelId && quantityField);
  const childrenCtx: ResourceConfigModalChildrenContext = { quantityUnit };

  return (
    <PluginModal visible={visible} title={title} onOk={handleOk} onCancel={onCancel} focusLock={false} unmountOnExit style={{ width: 720 }}>
      <Form form={modalForm} layout="vertical">
        <div className={styles.modalBody}>
          {showVendorDevice ? (
            <div hidden={vendorDeviceHidden}>
              <VendorDeviceSelector
                form={modalForm}
                resourceCode={resourceCode}
                vendorDevicePrefix={vendorDevicePrefix}
                billModelId={billModelId}
                deploymentTypeId={deploymentTypeId}
                architecture={architecture}
                inventoryResourceType={inventoryResourceType}
                workloadType={workloadType}
                inventoryShowQuantity={inventoryShowQuantity}
                isContainerGpu={isContainerGpu}
                requireGpuSoftware={requireGpuSoftware}
                disabled={vendorDeviceDisabled}
                skipFetch={effectiveVendorSkipFetch}
              />
            </div>
          ) : null}

          {typeof children === 'function' ? children(modalForm, childrenCtx) : children}

          {showPricingQuantity ? (
            <Form.Item
              field={quantityField}
              label={quantityLabel ?? t('common:gpu.count')}
              rules={[{ required: true, message: t('common:required.field') }]}
              initialValue={1}
            >
              <InputNumber min={1} mode="button" placeholder={t('common:select.placeholder')} suffix={quantityUnit || undefined} />
            </Form.Item>
          ) : null}
        </div>

        {billModelId ? <VendorDevicePersistFields fieldPrefix={vendorDevicePrefix} /> : null}
        <OptionMetaRootField />
      </Form>
    </PluginModal>
  );
}
